// The one place the web app talks to the server.
//
// Every response either resolves to the parsed body or throws an ApiError with
// the code and the per-field messages the server sent, so forms can put the
// message under the right input instead of showing a wall of red at the top.

import type {
  AuthenticationResponseJSON,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
} from '@simplewebauthn/browser';

export interface ApiErrorPayload {
  code: string;
  message: string;
  details?: { fields?: Record<string, string> };
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly fields: Record<string, string> = {},
    /** Structured extras — the conflict panel arrives here. */
    public readonly details?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export async function request<T>(
  path: string,
  options: { method?: string; body?: unknown } = {}
): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`/api${path}`, {
      method: options.method ?? 'GET',
      headers: options.body === undefined ? {} : { 'Content-Type': 'application/json' },
      // The session cookie is httpOnly, so it rides along automatically —
      // but only if we ask for credentials.
      credentials: 'same-origin',
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
    });
  } catch {
    throw new ApiError(0, 'network', 'Could not reach Dayboard. Check your connection.');
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  let parsed: unknown = null;
  if (text.length > 0) {
    try {
      parsed = JSON.parse(text);
    } catch {
      throw new ApiError(response.status, 'bad_response', 'The server sent something unexpected.');
    }
  }

  if (!response.ok) {
    const payload = (parsed as { error?: ApiErrorPayload } | null)?.error;

    // An administrator who has not enrolled a second factor is answered this
    // by every route except the few that let them enrol or sign out
    // (docs/spec/11-account-security.md §1). Handled HERE rather than at each
    // call site because there are dozens of them and one that forgot would
    // show a bare "403 forbidden" on a page the person cannot fix from.
    //
    // A hard navigation rather than a router push: this module has no router,
    // and the destination deliberately drops whatever half-loaded screen the
    // person was on.
    if (
      payload?.code === 'totp_enrolment_required' &&
      typeof window !== 'undefined' &&
      window.location.pathname !== '/enrol-2fa'
    ) {
      window.location.assign('/enrol-2fa');
    }

    throw new ApiError(
      response.status,
      payload?.code ?? 'error',
      payload?.message ?? 'Something went wrong.',
      payload?.details?.fields ?? {},
      payload?.details
    );
  }

  return parsed as T;
}

/**
 * A multipart POST, through XMLHttpRequest rather than fetch, purely for the
 * progress events — fetch cannot report UPLOAD progress, and a 40MB drawing
 * over a site connection needs a bar rather than a spinner.
 *
 * It lives here beside `request` rather than in documentsApi because round 38
 * gave it a second caller: announcements now post their files with the post
 * itself. Two copies of this would be two answers to "what does a failed
 * upload say", and the second copy is always the one that says "undefined".
 */
export function postForm<T>(
  path: string,
  form: FormData,
  onProgress?: (fraction: number) => void,
  /** PATCH for an edit that may carry a replacement file (round 45, job invoices). */
  method: 'POST' | 'PATCH' = 'POST'
): Promise<T> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, `/api${path}`);
    xhr.withCredentials = true;

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) onProgress(event.loaded / event.total);
    });

    xhr.addEventListener('load', () => {
      if (xhr.status === 204) {
        resolve(undefined as T);
        return;
      }

      let parsed: unknown = null;
      try {
        parsed = JSON.parse(xhr.responseText) as unknown;
      } catch {
        reject(new ApiError(xhr.status, 'bad_response', 'The server sent something unexpected.'));
        return;
      }

      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(parsed as T);
        return;
      }

      const payload = (parsed as { error?: ApiErrorPayload } | null)?.error;
      reject(
        new ApiError(
          xhr.status,
          payload?.code ?? 'error',
          payload?.message ?? 'That upload did not work.',
          payload?.details?.fields ?? {},
          payload?.details
        )
      );
    });

    xhr.addEventListener('error', () =>
      reject(new ApiError(0, 'network', 'Could not reach Dayboard. Check your connection.'))
    );

    xhr.send(form);
  });
}

// --- Shapes the server returns ----------------------------------------

export type UserRole = 'admin' | 'manager' | 'field';
export type UserStatus = 'invited' | 'active' | 'deactivated';

export interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  status: UserStatus;
  emailVerified: boolean;
  lastActiveAt: string | null;
  createdAt: string;
  /**
   * Round 32 — may open the owner console. False for every customer forever;
   * it is here so the app knows whether to draw the link. Never an access
   * control: the server checks the same column and a second factor besides.
   */
  isPlatformAdmin?: boolean;
  /** Round 44. Whether they fill in a timesheet, as it applies, and the
      stored choice (null: the role's default). */
  onTimesheets?: boolean;
  onTimesheetsSetting?: boolean | null;
}

export interface Tenant {
  id: string;
  name: string;
  /** Optional and permanently so — null means the customer never gave one. */
  abn: string | null;
  /**
   * The rest of the letterhead printed on a form PDF (round 29). Optional on
   * the same terms as the ABN: null means "not given", and the document leaves
   * the line out rather than printing a blank one.
   */
  address: string | null;
  phone: string | null;
  email: string | null;
  /** Whether there is a logo to show. The key itself is the server's business. */
  hasLogo: boolean;
  timezone: string;
  /** Job numbering (round 36): format, the prefix 'prefixed' uses, and the
      counter's next value — enough for a live example of the next number. */
  jobNumberFormat: 'sequential' | 'prefixed' | 'yearly';
  jobNumberPrefix: string;
  nextJobNumber: number;
  jobNumberYear: number | null;
  /** Quotes (round 43): whether the company uses them at all, and their
      numbering and GST position. Off hides every quote screen. */
  //  Absent for a field worker, who has no business knowing the quote count.
  quoteNumberFormat?: 'sequential' | 'prefixed' | 'yearly';
  quoteNumberPrefix?: string;
  nextQuoteNumber?: number;
  quoteNumberYear?: number | null;
  quotesGstRegistered?: boolean;
  /** Timesheets (round 44). Sent to every role: a worker's nav needs it. */
  timesheetsEnabled: boolean;
  /** The day a timesheet week starts, 0 = Sunday. */
  timesheetWeekStart: number;
  subscriptionStatus: 'trialing' | 'active' | 'read_only' | 'cancelled';
  trialEndsAt: string;
  trialDaysRemaining: number;
  readOnly: boolean;
}

export interface Workspace {
  tenantId: string;
  tenantName: string;
}

export type SessionState =
  | { signedIn: false }
  | { signedIn: true; user: User; tenant: Tenant };

export type LoginResult =
  | { result: 'signed_in'; user: User; tenant: Tenant }
  | { result: 'workspace_required'; workspaces: Workspace[] }
  // The password was right and the account has a second factor. Not a
  // failure — the challenge is a five-minute token that authorises exactly
  // one thing, and no session exists until a code is produced.
  | {
      result: 'totp_required';
      challenge: string;
      /**
       * Which factors can answer it (round 32). A passkey and an authenticator
       * code are peers, so the screen offers whichever the account has.
       */
      factors?: { totp: boolean; passkey: boolean };
      /** Ready to hand to navigator.credentials.get — present only with a passkey. */
      passkeyOptions?: PublicKeyCredentialRequestOptionsJSON;
    };

/**
 * What every sensitive change carries.
 *
 * The password, and a current code when the account has a second factor. The
 * server decides which of those it needs; the forms send both when they have
 * them and read the per-field message back when they do not.
 */
export interface Reauth {
  currentPassword: string;
  totpCode?: string;
}

export interface PlatformCompany {
  id: string;
  name: string;
  status: 'trialing' | 'active' | 'read_only' | 'cancelled';
  founding: boolean;
  seats: { admin: number; manager: number; field: number };
  invited: number;
  dunning: boolean;
  latest: { number: string | null; period: string; totalCents: number; status: string } | null;
  invoicedThisMonthCents: number;
  outstandingCents: number;
  createdAt: string;
  trialEndsAt: string;
  storageUsedBytes: number;
  /** Round 43: whether quoting is on, and how much it is used. Counts only. */
  quotes: { created: number; converted: number };
}

export interface PlatformReadout {
  companies: PlatformCompany[];
  /** Companies that could not be read; each already reported to Sentry. */
  unreadable: number;
  timezone: string;
  readAt: string;
}

export interface Passkey {
  id: string;
  name: string;
  /** The authenticator says it syncs. Shown, never enforced. */
  backedUp: boolean;
  createdAt: string;
  lastUsedAt: string | null;
}

export interface SecurityStatus {
  totpEnabled: boolean;
  /** True for admins, who may rotate their authenticator but never remove it. */
  totpRequired: boolean;
  enabledAt: string | null;
  recoveryCodesRemaining: number;
  /** At or below this, the page starts telling them to generate more. */
  recoveryCodesLow: number;
  /** Round 32. Every role may hold these; nobody is required to. */
  passkeys: Passkey[];
}

// --- Calls ------------------------------------------------------------

export const api = {
  session: () => request<SessionState>('/auth/session'),

  signupStatus: () => request<{ signupsOpen: boolean; message?: string }>('/auth/signup'),

  signup: (body: {
    companyName: string;
    name: string;
    email: string;
    password: string;
    abn?: string;
    timezone?: string;
  }) => request<{ user: User; tenant: Tenant }>('/auth/signup', { method: 'POST', body }),

  /** The company's own details. Admin only. */
  /**
   * SEND ONLY WHAT IS BEING CHANGED. The server reads a missing key as
   * "unchanged", so the billing page's ABN panel and the company settings page
   * can each save their own fields without wiping the other's.
   */
  updateCompany: (body: {
    name?: string;
    abn?: string;
    address?: string;
    phone?: string;
    email?: string;
    timezone?: string;
    jobNumberFormat?: 'sequential' | 'prefixed' | 'yearly';
    jobNumberPrefix?: string;
    nextJobNumber?: number;
    quotesGstRegistered?: boolean;
    quoteNumberFormat?: 'sequential' | 'prefixed' | 'yearly';
    quoteNumberPrefix?: string;
    nextQuoteNumber?: number;
    timesheetsEnabled?: boolean;
    timesheetWeekStart?: number;
  }) => request<{ tenant: Tenant }>('/company', { method: 'PATCH', body }),

  uploadCompanyLogo: async (file: File): Promise<{ tenant: Tenant }> => {
    const form = new FormData();
    form.append('file', file, file.name);
    let response: Response;
    try {
      // No Content-Type header: the browser adds the multipart boundary.
      response = await fetch('/api/company/logo', {
        method: 'PUT',
        credentials: 'same-origin',
        body: form,
      });
    } catch {
      throw new ApiError(0, 'network', 'Could not reach Dayboard. Check your connection.');
    }
    const text = await response.text();
    const parsed = text.length > 0 ? (JSON.parse(text) as Record<string, unknown>) : null;
    if (!response.ok) {
      const payload = (parsed as { error?: ApiErrorPayload } | null)?.error;
      throw new ApiError(
        response.status,
        payload?.code ?? 'error',
        payload?.message ?? 'Could not upload that logo.',
        payload?.details?.fields ?? {}
      );
    }
    return parsed as unknown as { tenant: Tenant };
  },

  deleteCompanyLogo: () => request<{ tenant: Tenant }>('/company/logo', { method: 'DELETE' }),

  login: (body: { email: string; password: string; tenantId?: string }) =>
    request<LoginResult>('/auth/login', { method: 'POST', body }),

  /** Answers the second-factor challenge with a code, or a recovery code. */
  answerTotpChallenge: (body: { challenge: string; code?: string; recoveryCode?: string }) =>
    request<LoginResult>('/auth/totp', { method: 'POST', body }),

  logout: () => request<void>('/auth/logout', { method: 'POST' }),

  // --- Account security (docs/spec/11-account-security.md) ---

  security: () => request<SecurityStatus>('/me/security'),

  changePassword: (body: Reauth & { newPassword: string }) =>
    request<{ changed: boolean }>('/me/password', { method: 'POST', body }),

  totpSetup: (body: Reauth) =>
    request<{ secret: string; otpauthUri: string }>('/me/totp/setup', { method: 'POST', body }),

  /** The recovery codes come back here, once, and are never retrievable again. */
  totpEnable: (body: Reauth & { code: string }) =>
    request<{ enabled: boolean; recoveryCodes: string[] }>('/me/totp/enable', {
      method: 'POST',
      body,
    }),

  totpDisable: (body: Reauth) =>
    request<{ disabled: boolean }>('/me/totp/disable', { method: 'POST', body }),

  // --- Passkeys (round 32) ---
  //
  // Two calls per enrolment because the ceremony is two round trips by
  // construction: the server issues a challenge, the authenticator signs it.
  passkeyOptions: (body: Reauth) =>
    request<{ token: string; options: PublicKeyCredentialCreationOptionsJSON }>(
      '/me/passkeys/options',
      { method: 'POST', body }
    ),

  passkeyAdd: (body: { token: string; response: RegistrationResponseJSON; name?: string }) =>
    request<{ passkey: { name: string } }>('/me/passkeys', { method: 'POST', body }),

  passkeyRemove: (id: string, body: Reauth) =>
    request<{ removed: boolean }>(`/me/passkeys/${id}/remove`, { method: 'POST', body }),

  passkeyLogin: (body: { challenge: string; response: AuthenticationResponseJSON }) =>
    request<LoginResult>('/auth/passkey', { method: 'POST', body }),

  // --- The owner console (round 32) ---
  //
  // 404 for everyone who is not the one account holding the flag, so a failure
  // here is expected rather than exceptional for every other user.
  platformCompanies: () => request<PlatformReadout>('/platform/companies'),

  regenerateRecoveryCodes: (body: Reauth) =>
    request<{ recoveryCodes: string[] }>('/me/recovery-codes', { method: 'POST', body }),

  requestEmailChange: (body: Reauth & { email: string }) =>
    request<{ pendingEmail: string }>('/me/email/request', { method: 'POST', body }),

  confirmEmailChange: (token: string) =>
    request<{ changed: boolean; email: string }>('/auth/confirm-email-change', {
      method: 'POST',
      body: { token },
    }),

  /** Another admin clearing a colleague's second factor. Their OWN credentials. */
  resetUserTotp: (id: string, body: Reauth) =>
    request<{ reset: boolean; reason?: string }>(`/users/${id}/totp-reset`, {
      method: 'POST',
      body,
    }),

  // The break-glass path, all unauthenticated — the person using it cannot
  // sign in, which is the entire point.
  requestTwofaReset: (email: string) =>
    request<{ requested: boolean }>('/auth/twofa-reset/request', {
      method: 'POST',
      body: { email },
    }),

  cancelTwofaReset: (token: string) =>
    request<{ cancelled: boolean }>('/auth/twofa-reset/cancel', {
      method: 'POST',
      body: { token },
    }),

  confirmTwofaReset: (token: string) =>
    request<{ completed: boolean; readyAt?: string }>('/auth/twofa-reset/confirm', {
      method: 'POST',
      body: { token },
    }),

  verifyEmail: (token: string) =>
    request<{ verified: boolean }>('/auth/verify-email', { method: 'POST', body: { token } }),

  resendVerification: () =>
    request<{ sent: boolean }>('/auth/resend-verification', { method: 'POST', body: {} }),

  requestPasswordReset: (email: string) =>
    request<{ sent: boolean }>('/auth/password-reset/request', {
      method: 'POST',
      body: { email },
    }),

  confirmPasswordReset: (body: { token: string; password: string }) =>
    request<{ reset: boolean }>('/auth/password-reset/confirm', { method: 'POST', body }),

  acceptInvitation: (body: { token: string; name?: string; password: string }) =>
    request<{ user: User; tenant: Tenant }>('/auth/accept-invitation', { method: 'POST', body }),

  /** Your own display name. No re-auth: a name is not a credential. */
  updateMe: (body: { name: string }) =>
    request<{ user: User }>('/me', { method: 'PATCH', body }),

  listUsers: () => request<{ users: User[] }>('/users'),

  inviteUser: (body: { name: string; email: string; role: UserRole }) =>
    request<{ user: User }>('/users', { method: 'POST', body }),

  updateUser: (
    id: string,
    body: {
      name?: string;
      role?: UserRole;
      status?: 'active' | 'deactivated';
      onTimesheets?: boolean | null;
    }
  ) => request<{ user: User }>(`/users/${id}`, { method: 'PATCH', body }),

  // The one-tap sample dataset (spec 09). Admin only, like the settings page
  // it lives on.
  sampleDataStatus: () => request<{ loaded: boolean }>('/sample-data'),
  loadSampleData: () => request<{ loaded: boolean }>('/sample-data', { method: 'POST' }),
  deleteSampleData: () => request<void>('/sample-data', { method: 'DELETE' }),
};
